import { useTranslation } from 'react-i18next';
// eslint-disable-next-line no-restricted-imports
import { Card } from 'semantic-ui-react';

// Import components
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useEffect, useState } from 'react';
import { EnforceAutomaticPayment, PaymentDigestSettings, ProcessingFeeSettings } from '../Components/Billing/Settings';
import CustomDepositAccountForm from '../Components/Billing/Settings/CustomDepositAccountForm';
import VerifyPayoutBanner from '../Components/SetupPayoutBanner/VerifyPayoutBanner';
import { BusyIndicator } from '../Components/Shared/BusyIndicator';
import { functions } from '../api/firebase/firebase';
import { getCreatePayoutAccountUrl } from '../api/firebase/organizations';
import { isPayoutVerified } from '../helpers/stripe';
import { useDelayedPayoutSetup } from '../hooks/useDelayedPayoutSetup';
import { useOrganization } from '../hooks/useOrganizations';
import { logError } from '../rollbar';
import { SEGMENT_EVENTS, useSegmentTrack } from '../segment';

import {
  Badge,
  CheckSuccessCircleIcon,
  ColorPaletteEnum,
  CreditCardIcon,
  Button as DSButton,
  EditIcon,
  EmailIcon,
  LockIcon,
  WidgetSizeEnum,
} from '@wonderschool/common-base-ui';

import './Finances.scss';

// Payments service + payout account + automatic-payment / processing-fee settings.
//
// This is no longer a standalone page: PAYM-5075 folded it into Billing Settings
// as the "Payments & Payouts" tab, since /billing/settings was its only route and
// the new late-payment-policy page took that path. The page chrome (Helmet title,
// PageTitle, useSegmentPage) moved to the parent — leaving it here would double
// the page title and fire two Segment page events.
const Finances = (_props) => {
  const { customDepositAccountUpdate = false } = useFlags();
  const { t } = useTranslation();
  const organization = useOrganization();
  const segmentTrack = useSegmentTrack();

  const [isLoading, setIsLoading] = useState();
  const [isRedirecting, setIsRedirecting] = useState();
  const [payoutAccount, setPayoutAccount] = useState();
  const [editingOrganizationId, setEditingOrganizationId] = useState(null);
  const [payoutRefresh, setPayoutRefresh] = useState(0);
  const [payoutUpdated, setPayoutUpdated] = useState(false);
  const [payoutLoadError, setPayoutLoadError] = useState(false);

  useEffect(() => setPayoutUpdated(false), [organization?.id]);

  const accountType = payoutAccount?.accountType ?? organization?.stripe?.accountType;
  const hasConnectedAccount = Boolean(organization?.stripe?.accountId);
  const hasPayoutDetails = Boolean(payoutAccount?.last4);
  const needsAccountOnboarding =
    !hasConnectedAccount ||
    (accountType === 'express' && payoutAccount?.accountDetailsSubmitted === false && !hasPayoutDetails);
  const canEditCustomAccount =
    customDepositAccountUpdate === true &&
    accountType === 'custom' &&
    isPayoutVerified(organization) &&
    payoutAccount?.accountCountry === 'US' &&
    payoutAccount?.accountCurrency === 'usd';

  useEffect(() => {
    let cancelled = false;
    setPayoutAccount(undefined);
    setEditingOrganizationId(null);
    setPayoutLoadError(false);
    const fetchPayoutAccountDetails = async () => {
      if (!organization?.stripe?.accountId) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      try {
        const result = await functions().httpsCallable('callables-stripe-getPayoutAccountDetails')({
          organizationId: organization.id,
        });
        if (!cancelled) setPayoutAccount(result?.data);
      } catch {
        if (!cancelled) {
          logError('Unable to load payout account details', undefined, { organizationId: organization.id });
          setPayoutLoadError(true);
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchPayoutAccountDetails();
    return () => {
      cancelled = true;
    };
  }, [organization?.id, organization?.stripe?.accountId, payoutRefresh]);

  const renderManagePayoutAccount = (label, className) => {
    if (accountType === 'express') {
      return (
        <a
          href="https://connect.stripe.com/express_login"
          target="_blank"
          rel="noreferrer"
          className={className}
          data-testid="edit-button"
        >
          <EditIcon className="my-auto mr-0.5 size-4" />
          {label}
        </a>
      );
    }

    if (canEditCustomAccount) {
      return (
        <DSButton label={label} data-testid="edit-button" onClick={() => setEditingOrganizationId(organization.id)} />
      );
    }

    return (
      <p className="ml-auto text-sm text-gray-700" data-testid="payout-account-support">
        {t('billing.settings.contactSupportToUpdateAccount')}{' '}
        <a href="mailto:support@wonderschool.com" className="text-blue-700 underline">
          {t('support@wonderschool.com')}
        </a>
      </p>
    );
  };

  const handleConnectClick = async () => {
    setIsRedirecting(true);
    try {
      segmentTrack(SEGMENT_EVENTS.addPayoutAccountSettings);
      const setupPayoutUri = await getCreatePayoutAccountUrl(organization.id);
      window.location.href = setupPayoutUri;
    } catch (error) {
      setIsRedirecting(false);
      logError('Error starting Stripe connect flow', error, { error });
    }
  };

  // Delayed payout setup: when on, billing is ungated (no approval gate) and the
  // payout-connect card requires the provider to be verified by support. Unverified
  // providers can request verification here at any time; invoices are not a
  // prerequisite for Stripe onboarding. When off, today's behavior.
  const delayedPayoutEnabled = useDelayedPayoutSetup();
  const isBillingUngated = delayedPayoutEnabled || isPayoutVerified(organization);
  // The org document lands via ORGANIZATION_FETCHED (fetching: true -> false). Until then the
  // Redux org has no `stripe` key, so isPayoutVerified reads false for verified providers too.
  // Never infer "unverified" from a not-yet-loaded org: the verify banner's CTA creates a
  // pending request and pings support, so it must only ever face genuinely unverified orgs.
  const isOrganizationLoaded = organization?.fetching === false;

  const renderHeaderRight = () => {
    if (isLoading || isRedirecting) return <BusyIndicator />;
    if (payoutLoadError) return null;

    if (needsAccountOnboarding)
      return (
        <div className="w-full sm:ml-auto sm:w-auto">
          <DSButton
            primary
            size={WidgetSizeEnum.MEDIUM}
            data-testid="connect-button"
            onClick={handleConnectClick}
            extraClasses="w-full sm:w-auto"
            disabled={!isBillingUngated}
            label={t(hasConnectedAccount ? 'billing.settings.continueSetup' : 'billing.settings.connectButton')}
          />
        </div>
      );

    if (isBillingUngated) return renderManagePayoutAccount(t('common.edit'), 'edit-button');
  };

  const renderPaymentAccountContent = () => {
    if (isLoading) return;
    if (payoutLoadError) return <p role="alert">{t('billing.depositAccount.loadError')}</p>;

    if (hasPayoutDetails) {
      const isCard = payoutAccount.type === 'card';
      return (
        <div className="account-section">
          <div className="column" data-testid="payment-account-type">
            <b>{isCard ? t('billing.settings.card') : t('billing.settings.bank')}</b>
            {payoutAccount.name}
          </div>
          <div className="column" data-testid="payment-account-number">
            <b>{t('billing.settings.accountNumber')}</b>
            **** {payoutAccount.last4}
          </div>
        </div>
      );
    }

    return (
      <span>
        {t(
          hasConnectedAccount
            ? needsAccountOnboarding
              ? 'billing.settings.continueSetupPrompt'
              : 'billing.settings.payoutDetailsUnavailable'
            : 'billing.settings.connectAccountPrompt'
        )}
      </span>
    );
  };

  const renderPaymentsServiceSection = () => (
    <Card fluid>
      <Card.Content style={{ display: 'flex' }}>
        {isBillingUngated ? null : <LockIcon className="icon-right lock-icon" />}
        <Card.Header as="h3" className="header-with-icon" data-testid="payments-service-title">
          {t('billing.settings.paymentsServiceTitle')}
        </Card.Header>
        {isBillingUngated ? (
          <span className="enabled-text" data-testid="payments-service-enabled">
            {t('billing.settings.enabled')}
          </span>
        ) : (
          <span className="disabled-text" data-testid="payments-service-disabled">
            {t('billing.settings.disabled')}
          </span>
        )}
      </Card.Content>
      {isBillingUngated ? null : (
        <Card.Content className="content-no-border">
          <span data-testid="payments-service-prompt">{t('billing.settings.enablePaymentsPrompt')}</span>
          <div className="link-section">
            <a
              href="mailto:support@wonderschool.com"
              target="_blank"
              rel="noreferrer"
              style={{ display: 'flex' }}
              data-testid="payments-service-email"
            >
              <EmailIcon className="email-icon" />
              {t('support@wonderschool.com')}
            </a>
          </div>
        </Card.Content>
      )}
    </Card>
  );

  const renderPayoutUpdateStatus = () =>
    payoutUpdated && (
      <p role="status" className="mt-4 text-green-700">
        {t('billing.depositAccount.saved')}
      </p>
    );

  const renderPaymentAccountSection = () => (
    <Card fluid className={`${isBillingUngated ? '' : 'disabled-section'}${isLoading ? 'placeholder-padding' : ''}`}>
      <Card.Content className="flex-wrap items-center gap-3" style={{ display: 'flex' }}>
        {isBillingUngated ? null : <LockIcon className="icon-right lock-icon" />}
        <Card.Header as="h3" className="header-with-icon" data-testid="payment-account-title">
          {t('billing.settings.paymentAccountTitle')}
        </Card.Header>
        {renderHeaderRight()}
      </Card.Content>
      <Card.Content className="content-no-border" style={{ paddingTop: 0 }}>
        {renderPaymentAccountContent()}
        {renderPayoutUpdateStatus()}
      </Card.Content>
    </Card>
  );

  // Polished payout card for the delayed-payout-setup flow (design-system tokens).
  // Only used on the flag-on verified path, so flag-off behavior is unchanged.
  const renderPaymentAccountSectionV2 = () => {
    return (
      <div className="overflow-hidden rounded-2xl border border-[var(--border-decorative-light)] bg-white shadow-sm">
        <div className="flex items-center gap-3 border-b border-[var(--border-decorative-light)] px-6 py-5">
          <CheckSuccessCircleIcon className="h-6 w-6 flex-none text-green-600" />
          <h3 className="text-base font-bold text-gray-900" data-testid="payment-account-title">
            {t('billing.settings.paymentAccountTitle')}
          </h3>
          {hasPayoutDetails && (
            <span className="ml-auto">
              <Badge label={t('billing.settings.connectedBadge')} color={ColorPaletteEnum.GREEN} />
            </span>
          )}
        </div>

        <div className="px-6 py-5">
          {isLoading ? (
            <BusyIndicator />
          ) : payoutLoadError ? (
            <p role="alert">{t('billing.depositAccount.loadError')}</p>
          ) : hasConnectedAccount && !needsAccountOnboarding && !hasPayoutDetails ? (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">{t('billing.settings.payoutDetailsUnavailable')}</p>
              {renderManagePayoutAccount(
                t('billing.settings.managePayoutAccount'),
                'flex items-center gap-1 text-sm font-semibold text-blue-700'
              )}
            </div>
          ) : hasPayoutDetails ? (
            <div className="flex items-center justify-between rounded-xl border border-[var(--border-decorative-light)] px-4 py-3">
              <div className="flex items-center gap-3">
                <CreditCardIcon className="h-6 w-6 flex-none text-gray-700" />
                <div>
                  <div className="text-sm font-semibold text-gray-900" data-testid="payment-account-type">
                    {payoutAccount.type === 'card' ? t('billing.settings.card') : t('billing.settings.bank')}{' '}
                    {payoutAccount.name}
                  </div>
                  <div className="text-xs text-[var(--text-muted)]" data-testid="payment-account-number">
                    {t('billing.settings.accountNumber')} ****{payoutAccount.last4}
                  </div>
                </div>
              </div>
              {renderManagePayoutAccount(
                t('billing.settings.managePayoutAccount'),
                'flex items-center gap-1 text-sm font-semibold text-blue-700'
              )}
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-700">
                {t(hasConnectedAccount ? 'billing.settings.continueSetupPrompt' : 'billing.settings.connectPromptV2')}
              </p>
              <div className="flex items-center gap-2 text-xs text-[var(--text-muted)]">
                <LockIcon className="h-4 w-4 flex-none" />
                <span>{t('billing.settings.stripeSecureNote')}</span>
              </div>
              {isRedirecting ? (
                <BusyIndicator />
              ) : (
                <DSButton
                  primary
                  size={WidgetSizeEnum.MEDIUM}
                  data-testid="connect-button"
                  onClick={handleConnectClick}
                  label={t(hasConnectedAccount ? 'billing.settings.continueSetup' : 'billing.settings.connectButton')}
                  preIcon={<CreditCardIcon className="h-4 w-4" />}
                />
              )}
            </div>
          )}
          {renderPayoutUpdateStatus()}
        </div>
      </div>
    );
  };

  return (
    <div>
      {editingOrganizationId === organization?.id && canEditCustomAccount && (
        <CustomDepositAccountForm
          key={organization.id}
          organizationId={organization.id}
          onClose={() => setEditingOrganizationId(null)}
          onSaved={() => {
            setEditingOrganizationId(null);
            setPayoutUpdated(true);
            setPayoutRefresh((value) => value + 1);
          }}
        />
      )}
      {renderPaymentsServiceSection()}
      {delayedPayoutEnabled ? (
        isPayoutVerified(organization) ? (
          // Verified: show the polished connect/account card regardless of deposit
          // history (don't suppress it for orgs verified under the old up-front flow).
          renderPaymentAccountSectionV2()
        ) : isOrganizationLoaded ? (
          // Unverified: always offer the verification request. Invoices are not a
          // prerequisite for Stripe onboarding, and this settings page is where a
          // provider comes looking for payout setup, so it must never be a dead end.
          <VerifyPayoutBanner />
        ) : null
      ) : (
        renderPaymentAccountSection()
      )}
      <EnforceAutomaticPayment />
      <PaymentDigestSettings key={`payment-digest-${organization?.id}`} />
      <ProcessingFeeSettings />
    </div>
  );
};

export default Finances;
